export const SIZE = 1080;
export const SLIDE_MS = 3300;

const cssVar = (n, fb) => getComputedStyle(document.documentElement).getPropertyValue(n).trim() || fb;
const ease = (t) => 1 - (1 - t) ** 3;

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

export const buildSlides = (y, name, joy = [], garden = null) => {
  const s = [{ kind: "title", title: `${name}'s year`, sub: `${y.label} · ${y.checkins} check-ins` }];
  s.push({ kind: "strip", title: "Month by month", months: y.months });
  if (y.top_emotions.length) s.push({ kind: "list", title: "Top emotions", items: y.top_emotions.slice(0, 4).map((e) => `${e.emoji}  ${e.name} · ${e.count}`) });
  if (y.best_month) s.push({ kind: "big", title: "Brightest month", big: "☀️", sub: y.best_month });
  if (y.best_skill) s.push({ kind: "big", title: "What helped most", big: "🧘", sub: y.best_skill.name, note: `stress −${y.best_skill.avg_drop} on average` });
  if (garden) {
    const c = garden.counts;
    s.push({ kind: "garden", title: "Your year garden", img: garden.img,
      sub: [plural(c.flowers, "flower"), c.golden && plural(c.golden, "golden marigold"), c.friends && plural(c.friends, "friendship bloom")].filter(Boolean).join(" · ") });
  }
  joy.forEach((m) => s.push({ kind: "photos", title: `Joy Snaps · ${m.label}`, photos: m.photos,
    sub: m.photos.length === 1 ? m.photos[0].caption : `${m.count} joyful moment${m.count === 1 ? "" : "s"}` }));
  s.push({ kind: "big", title: "Thank you for showing up", big: "💜", sub: "Every check-in was an act of care", note: "Mindful Me" });
  return s;
};

const drawCover = (x, img, dx, dy, w, h) => {
  const r = Math.max(w / img.width, h / img.height), sw = w / r, sh = h / r;
  x.save(); x.beginPath(); x.roundRect(dx, dy, w, h, 28); x.clip();
  x.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, dx, dy, w, h);
  x.restore();
};

const drawPhotos = (x, slide, p, c, font) => {
  const n = slide.photos.length, cols = n === 1 ? 1 : n <= 4 ? 2 : 3, rows = Math.ceil(n / cols), gap = 20;
  const areaW = n === 1 ? 560 : 840, areaH = 560, w = (areaW - gap * (cols - 1)) / cols, h = (areaH - gap * (rows - 1)) / rows;
  slide.photos.forEach((ph, i) => {
    const show = ease(Math.max(0, Math.min(1, p * 4 - i * 0.25)));
    const col = i % cols, row = Math.floor(i / cols), inRow = Math.min(cols, n - row * cols);
    const ox = (SIZE - (inRow * w + gap * (inRow - 1))) / 2;
    x.globalAlpha = show;
    drawCover(x, ph.img, ox + col * (w + gap), 270 + row * (h + gap) + (1 - show) * 30, w, h);
  });
  if (slide.sub) { x.globalAlpha = ease(Math.min(1, p * 3)); x.fillStyle = c.muted; x.font = `italic 36px ${font}`; x.fillText(slide.sub, SIZE / 2, 885, SIZE - 200); }
};

const drawSlide = (x, slide, p, c, font) => {
  const a = ease(Math.min(1, p * 3));
  x.globalAlpha = a;
  x.textAlign = "center"; x.textBaseline = "middle";
  const rise = (1 - a) * 40;
  x.fillStyle = c.primary; x.font = `bold 64px ${font}`;
  x.fillText(slide.title, SIZE / 2, (slide.kind === "title" ? 470 : 200) + rise, SIZE - 160);
  x.fillStyle = c.text;
  if (slide.kind === "title") {
    x.font = `42px ${font}`; x.fillText(slide.sub, SIZE / 2, 570 + rise, SIZE - 160);
  } else if (slide.kind === "strip") {
    slide.months.forEach((m, i) => {
      const show = ease(Math.max(0, Math.min(1, p * 4 - i * 0.18)));
      const cx = 150 + (i % 6) * 156, cy = 450 + Math.floor(i / 6) * 260;
      x.globalAlpha = show;
      x.font = `76px ${font}`; x.fillText(m.top ? m.top.emoji : "·", cx, cy - 20 * (1 - show));
      x.fillStyle = c.muted; x.font = `600 30px ${font}`; x.fillText(m.label, cx, cy + 80); x.fillStyle = c.text;
    });
  } else if (slide.kind === "garden") {
    const w = 880, h = (w * slide.img.height) / slide.img.width;
    drawCover(x, slide.img, (SIZE - w) / 2, 300 + rise, w, h);
    x.fillStyle = c.muted; x.font = `600 38px ${font}`; x.fillText(slide.sub, SIZE / 2, 300 + h + 70 + rise, SIZE - 160);
  } else if (slide.kind === "photos") {
    drawPhotos(x, slide, p, c, font);
  } else if (slide.kind === "list") {
    slide.items.forEach((t, i) => {
      x.globalAlpha = ease(Math.max(0, Math.min(1, p * 4 - i * 0.4)));
      x.font = `50px ${font}`; x.fillText(t, SIZE / 2, 380 + i * 130, SIZE - 160);
    });
  } else {
    x.font = `${170 + 20 * Math.sin(p * Math.PI)}px ${font}`; x.fillText(slide.big, SIZE / 2, 470);
    x.font = `bold 56px ${font}`; x.fillText(slide.sub, SIZE / 2, 700 + rise, SIZE - 160);
    if (slide.note) { x.fillStyle = c.muted; x.font = `38px ${font}`; x.fillText(slide.note, SIZE / 2, 790 + rise, SIZE - 160); }
  }
  x.globalAlpha = 1;
};

export const drawFrame = (x, slides, t) => {
  const c = { bg: cssVar("--mm-bg", "#F8F5FC"), surface: cssVar("--mm-surface", "#FFFFFF"), primary: cssVar("--mm-primary", "#7C5CBF"),
    text: cssVar("--mm-text", "#1a1a2e"), muted: cssVar("--mm-text-secondary", "#8E8E9A") };
  const font = getComputedStyle(document.body).fontFamily || "sans-serif";
  const i = Math.min(slides.length - 1, Math.floor(t / SLIDE_MS));
  x.fillStyle = c.bg; x.fillRect(0, 0, SIZE, SIZE);
  x.fillStyle = c.surface; x.beginPath(); x.roundRect(60, 60, SIZE - 120, SIZE - 120, 56); x.fill();
  drawSlide(x, slides[i], (t - i * SLIDE_MS) / SLIDE_MS, c, font);
  x.fillStyle = c.primary;
  slides.forEach((_, k) => { x.globalAlpha = k <= i ? 1 : 0.25; x.beginPath(); x.roundRect(SIZE / 2 - slides.length * 22 + k * 44, 960, 32, 8, 4); x.fill(); });
  x.globalAlpha = 1;
};
